import React from 'react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
          <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg border border-red-200 overflow-hidden">
            <div className="bg-red-50 border-b border-red-200 px-6 py-4">
              <h1 className="text-xl font-bold text-red-800">Something went wrong</h1>
              <p className="text-sm text-red-600 mt-1">The application encountered an unexpected error.</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                <p className="text-sm font-mono text-red-700 break-words">
                  {this.state.error?.message || 'Unknown error'}
                </p>
              </div>
              {this.state.error?.stack && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-gray-500 hover:text-gray-700 font-medium">
                    Stack trace
                  </summary>
                  <pre className="mt-2 p-3 bg-gray-50 rounded-lg overflow-auto max-h-48 text-gray-600 border border-gray-200">
                    {this.state.error.stack}
                  </pre>
                </details>
              )}
              {this.state.errorInfo?.componentStack && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-gray-500 hover:text-gray-700 font-medium">
                    Component stack
                  </summary>
                  <pre className="mt-2 p-3 bg-gray-50 rounded-lg overflow-auto max-h-48 text-gray-600 border border-gray-200">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
              <button
                onClick={() => {
                  this.setState({ hasError: false, error: null, errorInfo: null });
                  window.location.href = '/';
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
